import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";
import uniqBy from "lodash.uniqby";

import { Button, Card, Filter, FilterValues, Table, PageHeader, FilterTypes, QuickFilterTypes, Highlights } from "@cofano/cck";
import { Box } from "@mui/material";

import { TableId, useBackendTable } from "components/table";
import Helmet from "components/helmet/Helmet";
import PopoverList from "components/popover/PopoverList";
import useLayout from "components/layout/useLayout";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import SalesCharterStatusChanger from "view/charters/components/status/SalesCharterStatusChanger";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import ProductSelect, { ProductSelectOption } from "view/products/components/select/ProductSelect";
import SalesCharterStatusSelect, { SalesCharterStatusSelectOption } from "view/charters/components/selects/SalesCharterStatusSelect";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import useOpenCharterSideviewer from "view/charters/components/sideviewer/useOpenCharterSideviewer";

import { DataFormat, Fields, hasOptionalFieldPermission, Language, usePermission } from "imports/Utils";

import { DossierLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { DateUtils, FormattedDate } from "utils/DateUtils";
import { productLinkWithDetails } from "utils/productUtils";

import { ChildPropsFromPagination } from "types/commons";
import { CharterSubtype, RelationType, SortDirection } from "__generated__/enums";
import { GetSalesChartersDocument, GetSalesChartersQuery } from "__generated__/view/charters/graphql/charter.queries.generated";

type SalesCharterFromQuery = ChildPropsFromPagination<GetSalesChartersQuery, "salesCharters">;

const SalesCharters = () => {
	const client = useApolloClient();
	const navigate = useNavigate();
	const layout = useLayout();

	const session = useStoreState((state) => state.session);

	const [ selectedTableRow, selectTableRow ] = useState<SalesCharterFromQuery | null>(null);

	const { isReadOnly, isRTR } = usePermission();

	const showDossierNumber = hasOptionalFieldPermission(session, Fields.SALES_CHARTER, "dossierNumber");

	const openCharterSideviewer = useOpenCharterSideviewer();

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.SalesCharters,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetSalesChartersDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "firstLoadingDate",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							charterNumber: filter.charterNumber,
							charterSubtypes: filter.charterSubtypes,
							customerReference: filter.customerReference,
							dossierNumber: filter.dossierNumber,
							voyageNumber: filter.voyageNumber,
							products: filter.products?.map((option: ProductSelectOption) => option.id),
							debtors: filter.debtor?.map((option: RelationSelectOption) => option.id),
							brokers: filter.broker?.map((option: RelationSelectOption) => option.id),
							shortNotice: filter.shortNotice,
							firstLoadingDate: filter.firstLoadingDate && {
								from: DateUtils.getTimestamp(filter.firstLoadingDate[0]),
								to: DateUtils.getTimestamp(filter.firstLoadingDate[1]),
							},
							blDate: filter.blDate && {
								from: DateUtils.getTimestamp(filter.blDate[0]),
								to: DateUtils.getTimestamp(filter.blDate[1]),
							},
							statuses: filter.statuses?.map((status: SalesCharterStatusSelectOption) => status.name) ?? [],
							ships: filter.ships?.map((option: ShipSelectOption) => option.id),
						},
					},
				});

				callback({
					content: result.data.salesCharters.content ?? [],
					totalResults: result.data.salesCharters.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	useEffect(() => {
		if (!layout.drawerSettings.open && selectedTableRow) {
			selectTableRow(null);
		}
	}, [ layout.drawerSettings.open ]);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("SALES_CHARTERS")}
					<Helmet title={intl.formatMessage({ id: "SALES_CHARTERS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && isRTR && (
						<Button onClick={() => navigate("/view/salescharters/create")}>
							{Language("CREATE_CHARTER")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
							}, {
								id: "charterSubtypes",
								title: intl.formatMessage({ id: "SUBTYPE" }),
								renderFilterValue: (value) => value.map((item: CharterSubtype) => intl.formatMessage({ id: item })).join(", "),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "CUSTOMER" }),
								renderFilterValue: (value) => value.map((item: RelationSelectOption) => item.name).join(", "),
							}, {
								id: "broker",
								title: intl.formatMessage({ id: "BROKER" }),
								renderFilterValue: (value) => value.map((item: RelationSelectOption) => item.name).join(", "),
							}, {
								id: "statuses",
								title: intl.formatMessage({ id: "STATUS" }),
								renderFilterValue: (value) => value.map((item: SalesCharterStatusSelectOption) => intl.formatMessage({ id: item.name })).join(", "),
							}, {
								id: "shortNotice",
								title: intl.formatMessage({ id: "CARGO_CHARTER_SHORT_NOTICE" }),
								renderFilterValue: (value) => DataFormat.booleanAsText(value),
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "SHIP" }),
								renderFilterValue: (value) => value?.map((item: ShipSelectOption) => item.name).join(", "),
							}, {
								id: "customerReference",
								title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
							}, {
								id: "dossierNumber",
								title: intl.formatMessage({ id: "DOSSIER_NUMBER" }),
							}, {
								id: "voyageNumber",
								title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
							}, {
								id: "products",
								title: intl.formatMessage({ id: "PRODUCTS" }),
								renderFilterValue: (value) => value?.map((item: ProductSelectOption) => item.name).join(", "),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					highlightRow={row => row.id === selectedTableRow?.id ? Highlights.HIGHLIGHT : Highlights.NONE}
					onRowClick={row => {
						const salesCharter = row as SalesCharterFromQuery;
						openCharterSideviewer(salesCharter.id, () => refetch());
						selectTableRow(salesCharter);
					}}
					onRowDoubleClick={(row, event) => {
						const url = `/view/salescharters/${row.id}/details`;

						if (event.ctrlKey) {
							window.open(url);
						} else {
							navigate(url);
						}
					}}
					filterComponent={() => (
						<Filter {...filterProps}
							fields={[ {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "charterSubtypes",
								title: intl.formatMessage({ id: "CHARTER_TYPE" }),
								render: (value, onChange) => (
									<CharterSubtypeSelect
										value={value}
										onChange={options => onChange(options)}
										multiple
									/>
								),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "CUSTOMER" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										multiple />
								),
							}, {
								id: "broker",
								title: intl.formatMessage({ id: "BROKER" }),
								render: (value, onChange) => (
									<RelationSelect
										types={[ RelationType.Broker ]}
										value={value}
										onChange={options => onChange(options)}
										multiple />
								),
							}, {
								id: "statuses",
								title: intl.formatMessage({ id: "STATUS" }),
								render: (value, onChange) => (
									<SalesCharterStatusSelect
										value={value}
										multiple
										onChange={onChange}
									/>
								),
							}, {
								id: "shortNotice",
								title: intl.formatMessage({ id: "CARGO_CHARTER_SHORT_NOTICE" }),
								filterType: FilterTypes.BOOLEAN,
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "SHIP" }),
								render: (value, onFieldChange) => (
									<ShipSelect
										value={value}
										onChange={(options) => onFieldChange(options)}
										multiple />
								),
							},  {
								id: "firstLoadingDate",
								title: intl.formatMessage({ id: "CARGO_CHARTER_FIRST_PLANNED_LOADING_DATE" }),
								filterType: FilterTypes.DATE_RANGE,
							}, {
								id: "customerReference",
								title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "dossierNumber",
								title: intl.formatMessage({ id: "DOSSIER_NUMBER" }),
								filterType: FilterTypes.TEXT,
								disabled: !showDossierNumber,
							}, {
								id: "voyageNumber",
								title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "products",
								title: intl.formatMessage({ id: "PRODUCTS" }),
								render: (value, onChange) => (
									<ProductSelect
										value={value}
										onChange={options => onChange(options)}
										multiple />
								),
							} ]} />
					)}
					columns={[ {
						id: "charterNumber",
						render: (row: SalesCharterFromQuery) => <DossierLink charter={row} />,
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						sortKey: "charterNumber",
						width: 150,
						filterComponent: QuickFilterTypes.TEXT,
						sortable: true,
					}, {
						id: "debtor",
						title: intl.formatMessage({ id: "CUSTOMER" }),
						sortable: true,
						sortKey: "debtor.name",
						width: 200,
						render: (row: SalesCharterFromQuery) => <RelationLink relation={row.debtor} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "CUSTOMER" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "broker",
						title: intl.formatMessage({ id: "BROKER" }),
						sortable: true,
						sortKey: "broker.name",
						width: 200,
						render: (row: SalesCharterFromQuery) => <RelationLink relation={row.broker} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								types={[ RelationType.Broker ]}
								label={intl.formatMessage({ id: "BROKER" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "charterSubtypes",
						title: intl.formatMessage({ id: "CHARTER_TYPE" }),
						render: (row: SalesCharterFromQuery) => <CharterSubtypeTag type={row.subtype} />,
						width: 150,
						filterComponent: (value, onChange) => (
							<CharterSubtypeSelect
								value={value}
								label={intl.formatMessage({ id: "CHARTER_TYPE" })}
								onChange={options => onChange(options)}
								multiple
							/>
						),
					}, {
						id: "statuses",
						title: intl.formatMessage({ id: "STATUS" }),
						width: 180,
						render: (row: SalesCharterFromQuery) => <SalesCharterStatusChanger charter={row} onCompleted={refetch} />,
						filterComponent: (value, onChange) => (
							<SalesCharterStatusSelect
								multiple
								label={intl.formatMessage({ id: "STATUS" })}
								value={value}
								onChange={onChange} />
						),
					}, {
						id: "shortNotice",
						title: intl.formatMessage({ id: "CARGO_CHARTER_SHORT_NOTICE" }),
						sortable: true,
						sortKey: "shortNotice",
						width: 150,
						render: (row: SalesCharterFromQuery) => DataFormat.boolean(row.shortNotice),
						filterComponent: QuickFilterTypes.BOOLEAN,
					}, {
						id: "firstLoadingDate",
						title: intl.formatMessage({ id: "CARGO_CHARTER_FIRST_PLANNED_LOADING_DATE" }),
						width: 230,
						sortable: true,
						sortKey: "firstLoadingDate",
						render: (row: SalesCharterFromQuery) => <FormattedDate date={row.firstLoadingDate} format="ll" />,
						filterComponent: QuickFilterTypes.DATE_RANGE,
					}, {
						id: "blDate",
						title: intl.formatMessage({ id: "BL_DATE" }),
						width: 150,
						sortable: true,
						sortKey: "blDate",
						render: (row: SalesCharterFromQuery) => <FormattedDate date={row.blDate} format="ll" />,
						filterComponent: QuickFilterTypes.DATE_RANGE,
					}, {
						id: "ships",
						title: intl.formatMessage({ id: "SHIP" }),
						width: 150,
						render: (row: SalesCharterFromQuery) => (row.voyages?.length ?? 0) > 0 && <ShipLink ship={row.voyages?.[0].ship} />,
						filterComponent: (value, onChange) => (
							<ShipSelect
								multiple
								label={intl.formatMessage({ id: "SHIP" })}
								value={value}
								onChange={(options) => onChange(options)} />
						),
					}, {
						id: "voyageNumber",
						title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
						width: 150,
						render: (row: SalesCharterFromQuery) => <VoyageLink voyage={row.voyages?.[0]} />,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "products",
						title: intl.formatMessage({ id: "PRODUCTS" }),
						width: 200,
						render: (row: SalesCharterFromQuery) => (
							<PopoverList
								underline
								list={uniqBy(row.cargos, "product.id")}
								label={intl.formatMessage({ id: "PRODUCTS" })}
								render={item => productLinkWithDetails(item.product, item.productDetails ?? "")}
								limit={1} />
						),
						filterComponent: (value, onChange) => (
							<ProductSelect
								label={intl.formatMessage({ id: "PRODUCT" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "customerReference",
						title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
						width: 150,
						sortable: true,
						sortKey: "customerReference",
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "dossierNumber",
						title: intl.formatMessage({ id: "DOSSIER_NUMBER" }),
						width: 150,
						sortable: true,
						sortKey: "dossierNumber",
						disabled: !showDossierNumber,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "totalRevenue",
						title: intl.formatMessage({ id: "TOTAL_REVENUE" }),
						width: 150,
						sortable: true,
						sortKey: "totalRevenue",
						render: (row: SalesCharterFromQuery) => DataFormat.price(row.totalRevenue),
					}, {
						id: "openDeclarationCount",
						title: intl.formatMessage({ id: "OPEN_DECLARATIONS" }),
						width: 150,
						sortable: true,
						sortKey: "openDeclarationCount",
						render: (row: SalesCharterFromQuery) => row.openDeclarationCount || null,
					}, {
						id: "openInvoiceCount",
						title: intl.formatMessage({ id: "OPEN_INVOICES" }),
						width: 150,
						sortable: true,
						sortKey: "openInvoiceCount",
						render: (row: SalesCharterFromQuery) => row.openInvoiceCount || null,
					} ]} />
			</Card>
		</div>
	);
};

export default SalesCharters;
